import { TemplateRef } from '@angular/core';
import { PendingDisplayStatus, PendingEmailStatus } from '../models';
import { CustomSetFilterOption } from '@anduin/shared';

export interface ReasonCodeInfo {
  displayText: string;
  recommendedAction: string;
  isRetryable: boolean;
}

export const REASON_CODE_MAP: Record<string, ReasonCodeInfo> = {
  INVOICE_NOT_FOUND: {
    displayText: 'Invoice posting data not yet synced',
    recommendedAction: 'Wait for invoice data to sync, then retry',
    isRetryable: false,
  },
  NO_RECIPIENT: {
    displayText: 'No email recipient found',
    recommendedAction: 'Add a valid email recipient to the invoice and retry',
    isRetryable: true,
  },
  ZERO_INVOICE: {
    displayText: 'Zero-balance invoice',
    recommendedAction: 'No action required — zero-balance invoices cannot be emailed',
    isRetryable: false,
  },
  INVOICE_PAID: {
    displayText: 'Invoice already paid',
    recommendedAction: 'No action required — paid invoices are not emailed',
    isRetryable: false,
  },
  INVOICE_DEACTIVATED: {
    displayText: 'Invoice deactivated',
    recommendedAction: 'No action required — deactivated invoices cannot be emailed',
    isRetryable: false,
  },
  INVOICE_AGE_LIMIT: {
    displayText: 'Invoice exceeds age limit',
    recommendedAction: 'No action required — invoice exceeds the maximum age limit for email delivery',
    isRetryable: false,
  },
  FAILED_SENDING: {
    displayText: 'Email sending failed',
    recommendedAction: 'Retry sending the email',
    isRetryable: true,
  },
};

const FALLBACK_REASON: ReasonCodeInfo = {
  displayText: 'Unknown error',
  recommendedAction: 'Contact support if this issue persists',
  isRetryable: false,
};

export const DISPLAY_TEXT_TO_REASON_CODES: Record<string, string> = Object.fromEntries(
  Object.entries(REASON_CODE_MAP).map(([code, info]) => [info.displayText, code]),
);

export function getReasonCodesForDisplayTexts(displayTexts: string[]): string[] {
  return displayTexts.map((text) => DISPLAY_TEXT_TO_REASON_CODES[text]).filter(Boolean);
}

export function getReasonCodesMatchingSearch(searchTerm: string): string[] {
  const term = searchTerm.toLowerCase();
  return Object.entries(REASON_CODE_MAP)
    .filter(([, info]) => info.displayText.toLowerCase().includes(term))
    .map(([code]) => code);
}

export function getReasonInfo(reasonCode: string): ReasonCodeInfo {
  return REASON_CODE_MAP[reasonCode] ?? FALLBACK_REASON;
}

export function isRetryable(reasonCode: string): boolean {
  return getReasonInfo(reasonCode).isRetryable;
}

/**
 * Returns the display status for a pending email row.
 * PENDING records are shown as 'Overdue' or 'Delayed' based on time in queue.
 * DISMISSED and RESOLVED records are shown as their title-case equivalents.
 */
export function getPendingDisplayStatus(status: PendingEmailStatus, createdAt: string, overdueHours: number): PendingDisplayStatus {
  if (status === PendingEmailStatus.RETRYING) return 'Retrying...';
  if (status === PendingEmailStatus.DISMISSED) return 'Dismissed';
  if (status === PendingEmailStatus.RESOLVED) return 'Resolved';

  const hours = (Date.now() - new Date(createdAt).getTime()) / 3_600_000;
  return hours >= overdueHours ? 'Overdue' : 'Delayed';
}

export const PENDING_REASON_CODES = ['INVOICE_NOT_FOUND'];

export const FAILED_REASON_CODES = [
  'INVOICE_NOT_FOUND',
  'NO_RECIPIENT',
  'ZERO_INVOICE',
  'INVOICE_PAID',
  'INVOICE_DEACTIVATED',
  'INVOICE_AGE_LIMIT',
  'FAILED_SENDING',
];

function buildReasonCodeFilterOptions(
  codes: string[],
  labelTemplateRef: TemplateRef<unknown>,
): CustomSetFilterOption[] {
  return codes
    .filter((code) => REASON_CODE_MAP[code])
    .map((code) => ({
      value: code,
      display: { templateRef: labelTemplateRef, context: REASON_CODE_MAP[code].displayText },
      checked: false,
    }));
}

export function getPendingReasonCodeFilterOptions(labelTemplateRef: TemplateRef<unknown>): CustomSetFilterOption[] {
  return buildReasonCodeFilterOptions(PENDING_REASON_CODES, labelTemplateRef);
}

export function getFailedReasonCodeFilterOptions(labelTemplateRef: TemplateRef<unknown>): CustomSetFilterOption[] {
  return buildReasonCodeFilterOptions(FAILED_REASON_CODES, labelTemplateRef);
}
